import axios, { HttpStatusCode } from "axios";
import { API_BASE_URL } from "../API_CONSTANT";
import { toast } from "sonner";
import { refreshToken } from "../../common/services/auth";
import { getAutorization } from "../getAutorisation";

// Flag to track if a refresh token is currently being executed
// This is used to prevent multiple requests from trying to refresh the token simultaneously
let isRefreshing = false;
// Queue of requests waiting for the token refresh to complete
let failedQueue: {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  originalRequest: any;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      // 1. Update header with the new token
      // Note: On met à jour l'en-tête ici car la requête est relancée immédiatement
      // par la résolution de la promesse (iAxios(prom.originalRequest)).
      prom.originalRequest.headers = {
        ...prom.originalRequest.headers,
        Authorization: "Bearer " + token,
      };
      // 2. Mark as retried
      prom.originalRequest._retry = true;
      // 3. Resolve the promise to re-run the request with the new token
      prom.resolve(iAxios(prom.originalRequest));
    }
  });

  failedQueue = [];
};

const iAxios = axios.create({
  baseURL: API_BASE_URL,
});

export const setupInterceptors = () => {
  iAxios.interceptors.request.use((request) => {
    const theRequest: any = { ...request };

    // Si la requête n'est PAS un retry immédiat de la queue, on lit l'autorisation du localStorage.
    // Si elle est un retry, son header a déjà été mis à jour par processQueue.
    if (!theRequest._retry) {
      const authorizationData = getAutorization();

      if (authorizationData) {
        theRequest.headers = {
          ...request.headers,
          Authorization: "Bearer " + authorizationData?.token,
        };
      }
    }

    return theRequest;
  });

  iAxios.interceptors.response.use(
    (response) => {
      // Handle known successful status codes for redirection and success toasts
      if (
        response?.config?.method === "get" &&
        response?.status === HttpStatusCode.Forbidden
      ) {
        window.location.href = "/app/not-authorized";
      }
      if (
        response?.config?.method === "get" &&
        response?.status === HttpStatusCode.InternalServerError
      ) {
        window.location.href = "/app/error";
      }

      if (
        response?.config?.method !== "get" &&
        response?.data?.status?.message
      ) {
        toast.success(response?.data?.status?.message);
      }
      if (
        (response?.config?.method === "put" ||
          response?.config?.method === "patch") &&
        response?.status === HttpStatusCode.Ok
      ) {
        toast.success(
          response?.data?.status?.message ??
            "Modification effectuée avec succès"
        );
      }

      if (
        response?.config?.method === "post" &&
        response?.status === HttpStatusCode.Created
      ) {
        toast.success(
          response?.data?.status?.message ?? "Ajout effectué avec succès"
        );
      }
      return response;
    },
    async (error) => {
      // Use error.response?.status instead of error?.status for response status
      const status = error.response?.status;
      const originalRequest = error.config;

      // Handle Non-401 Errors first
      if (status === HttpStatusCode.InternalServerError) {
        if (originalRequest?.method === "get") {
          // window.location.href = "/app/error";
        } else {
          toast.error(
            error?.response?.data?.description || "Erreur serveur interne."
          );
        }
      } else if (status === 422) {
        // Validation error
        const messages = error?.response?.data?.violations?.map(
          (item: any) => item.message
        ) || ["Erreur de validation"];
        toast.error(messages.join(", "));
      } else if (status !== HttpStatusCode.Unauthorized) {
        // All other non-401 errors
        toast.error(
          error?.response?.data?.description ||
            "Une erreur inattendue est survenue."
        );
      }

      if (
        status === HttpStatusCode.Forbidden &&
        originalRequest?.method === "get"
      ) {
        // window.location.href = "/app/not-authorized";
      }

      // --- TOKEN REFRESH LOGIC (401 Unauthorized) ---
      if (
        status === HttpStatusCode.Unauthorized &&
        originalRequest?.method === "get"
      ) {
        const authorizationData = getAutorization();
        const refresh_token = authorizationData?.refresh_token;

        // 1. Si pas de refresh token ou déjà retenté (échec du retry), on déconnecte
        if (!refresh_token || originalRequest._retry) {
          localStorage.setItem("auth_data", "");
          window.location.href = "/auth/login";
          return Promise.reject(error);
        }

        // 2. Marque la requête originale pour le retry.
        originalRequest._retry = true;

        // 3. Enregistre la promesse de la requête dans la file d'attente
        const retryOriginalRequest = new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, originalRequest });
        });

        // 4. Si aucun rafraîchissement n'est en cours, on le lance
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            // Appel au service pour rafraîchir le token
            const newAuthData = await refreshToken(refresh_token);

            if (newAuthData && newAuthData.token) {
              // Mettre à jour le localStorage avec la bonne structure
              localStorage.setItem(
                "auth_data",
                JSON.stringify({
                  state: {
                    authorizationData: newAuthData,
                  },
                })
              );

              // Processus de la file d'attente
              processQueue(null, newAuthData.token);
            } else {
              // Échec du rafraîchissement
              processQueue(error);
              localStorage.setItem("auth_data", "");
              window.location.href = "/auth/login";
            }
          } catch (refreshError: any) {
            // Échec de l'appel API de rafraîchissement
            console.error(
              "Token refresh failed, redirecting to login.",
              refreshError
            );
            // On passe l'erreur pour rejeter toutes les promesses de la file d'attente
            processQueue(refreshError);
            localStorage.setItem("auth_data", "");
            window.location.href = "/auth/login";
          } finally {
            isRefreshing = false;
          }
        }

        // 5. Retourne la promesse de la file d'attente (soit la requête relancée, soit l'erreur)
        return retryOriginalRequest;
      }

      return Promise.reject(error);
    }
  );
};

export default iAxios;
